import posthog from "posthog-js"

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://eu.i.posthog.com"

/**
 * Initialize PostHog. Call once at app startup.
 * No-ops silently when the key is missing (local dev without env var).
 */
export function initPostHog(): void {
  if (!POSTHOG_KEY) return

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
  })
}

/**
 * Identify the logged-in user. Call after successful login / session restore.
 * If a chatbot visitor ID cookie (sf_vid) exists, alias it to merge the
 * anonymous chat session with the authenticated user in PostHog.
 */
export function identifyUser(user: { id: string; email?: string; name?: string }): void {
  if (!POSTHOG_KEY) return

  posthog.identify(user.id, {
    email: user.email,
    name: user.name,
  })

  // Merge chatbot visitor identity → authenticated user (one-time)
  try {
    const match = document.cookie.match(/(?:^| )sf_vid=([^;]+)/)
    if (match?.[1]) {
      const chatVisitorId = decodeURIComponent(match[1])
      posthog.alias(chatVisitorId)
      // Clear cookie after merge to avoid duplicate aliases on future logins
      document.cookie = "sf_vid=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax"
    }
  } catch {
    // Non-critical — alias failure doesn't break auth
  }
}

/**
 * Reset identity on logout.
 */
export function resetUser(): void {
  if (!POSTHOG_KEY) return
  posthog.reset()
}

/**
 * Track a custom event.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!POSTHOG_KEY) return
  posthog.capture(event, properties)
}

export { posthog }
